import { combineClasses, formatCurrency } from '~/util'

import styles from './UnitPrice.module.scss'
import { Nilable } from 'tsdef'
import { Tooltip, TooltipProps } from '../popovers'
import React from 'react'

interface MaybeTooltipProps extends Omit<TooltipProps, 'content'> {
  hiddenMessage: string
  hiddenMessageDetails: string
}

const MaybeTooltip = React.memo(
  ({
    hiddenMessage,
    hiddenMessageDetails,
    children,
    ...props
  }: MaybeTooltipProps) =>
    hiddenMessage ? (
      <Tooltip content={hiddenMessageDetails} {...props}>
        {children}
      </Tooltip>
    ) : (
      children
    )
)

interface UnitPriceProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Price per month of the unit in pennies.
   */
  price: Nilable<number>
  /**
   * Whether or not the unit is available for lease.
   */
  isAvailableForLease: boolean
  /**
   * Whether or not the unit is currently available to tour.
   */
  isAvailableForTour: boolean
  /**
   * Whether or not the community is configured to show unit prices.
   */
  showUnitPrices: boolean
  /**
   * Whether or not the current page should show units that cannot be toured.
   */
  showUntourableUnits: boolean
  /**
   * Whether the unit is a model.
   */
  isModel: boolean
  /**
   * Whether or not the community is configured to show model prices.
   */
  showModelPrices: boolean
}

/**
 * A helper for displaying the price value of a unit. It takes the price
 * in pennies and displays it in the user's locale as long as the price is
 * allowed to be shown for the unit.
 */
export function UnitPrice({
  className,
  price,
  isAvailableForLease,
  isAvailableForTour,
  showUnitPrices,
  showUntourableUnits,
  isModel,
  showModelPrices,
  ...rest
}: UnitPriceProps) {
  // Always hide prices if the community is configured to not show them
  if (!showUnitPrices) return null

  const hiddenMessage =
    isModel && !showModelPrices
      ? 'Call for more info'
      : !isModel && !isAvailableForLease
      ? 'No Longer Available'
      : !isAvailableForTour && !showUntourableUnits
      ? 'Pending Available'
      : ''
  const hiddenMessageDetails =
    isModel && !showModelPrices
      ? 'This is a model unit. Please call for availability of this floor plan.'
      : !isModel && !isAvailableForLease
      ? 'This unit is no longer available to lease.'
      : !isAvailableForTour && !showUntourableUnits
      ? 'This unit currently cannot be toured.'
      : ''

  // If we don't have a price and there's no hidden message to show,
  // then don't render anything.
  if (price == null && !hiddenMessage) return null

  return (
    <span
      data-testid="UnitPrice"
      className={combineClasses(
        styles.UnitPrice,
        hiddenMessage && styles.hidden,
        // Make sure the tooltip trigger is clickable within a card.
        styles.priceTooltipTrigger,
        className
      )}
      {...rest}
    >
      <MaybeTooltip
        hiddenMessage={hiddenMessage}
        hiddenMessageDetails={hiddenMessageDetails}
      >
        {hiddenMessage || formatCurrency(price! * 100)}
      </MaybeTooltip>
    </span>
  )
}
